import { PlatformLocation } from '@angular/common';
import { HttpProgressEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { logDiagnostic } from '@office-start/telemetry-service';
import { KnownRequestInstrumentationEvents, TelemetryProperties } from '@office-start/telemetry-types';
import { take } from 'rxjs/operators';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpDataResponse, InstrumentedHttpPromise, InstrumentedHttpPromiseCallback } from '../instrumentation/instrumented-http.service';
import { CheckAccessItem, CheckAccessResponse } from '../models/check-access-for-user.model';
import { ErrorDialogErrorCategory } from '../models/error-dialog.model';
import { FileItem, FileType } from '../models/file-item.model';
import { GraphError } from '../models/graph.model';
import { HttpRequestOptions } from '../models/http-request-options.model';
import { HttpResponseContentType } from '../models/http-response-content-type.enum';
import { KnownWorkloadReferralNames } from '../models/known-workload-referral-names.model';
import { GraphDriveItem, GraphDriveItemCollection, GraphUploadSession, GraphUploadSessionStatus } from '../models/onedrive-graph.model';
import { OperationStream } from '../models/operation-stream.model';
import { Action, ActionType, Activity, ActivityActor, Comment, Edit, Mention } from '../mru/activity.model';
import { MruFileFormat, MruFileItem, WopiHostData } from '../mru/mru-file-item.model';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { ContextService } from '../shared-context/context.service';
import { ObservableHelper } from '../utilities/observable-helper.utility';
import { PromiseCancellationToken } from '../utilities/promise-cancellation-token.utility';
import { UrlBuilder } from '../utilities/url-builder.utility';
import { UrlParser } from '../utilities/url-parser.utility';
import { AccessTokenConsumer, AccessTokenService } from './access-token.service';
import { DateService } from './date.service';
import { OfficeHomeApiConfigService } from './officehome-api-config.service';
import { GetDownloadUrlProps, OneDriveAction, OneDriveRootUrls, OneDriveService } from './onedrive.service';
import { SchedulePriority, SchedulerService } from './scheduler.service';
import { WindowService } from './window.service';
import { XhrService, XhrServiceError } from './xhr.service';

// TODO: maybe move this cache to a shared class to reuse in other services (ex: permissions API call)
interface VroomRequestCacheItem {
    promise: Promise<any>;
    expiryTime?: Date;
}

interface VroomActivityResponse {
    value: Array<VroomActivity>;
}

interface VroomActivity {
    action: { [name: string]: Edit | Comment | Mention };
    actor: ActivityActor;
    times: VroomActivityTimes;
}

interface VroomActivityTimes {
    observedTime: string;
    recordedTime: string;
}

interface VroomApiInfo {
    baseUrl: string;
    tokenType: AccessTokenConsumer;
    resourceUrl: string;
    hostname: string;
}

interface VroomSharingLink {
    application: any;
    preventsDownload: false;
    type: 'view' | 'edit' | 'embed';
    scope: 'anonymous' | 'organization';
    webHtml: string;
    webUrl: string;
}

interface VroomFluidEnabled {
    isFluidEnabled: boolean;
}

export interface VroomCreateLinkResponse {
    id: string;
    grantedTo: any;
    grantedToIdentities: Array<any>;
    inheritedFrom: any;
    invitation: any;
    link: VroomSharingLink;
    roles: Array<string>;
    shareId: string;
    expirationDateTime: string;
    password: string;
    hasPassword: boolean;
}

export const enum Scenario {
    BrowseOrgTemplates = 'BrowseOrgTemplates'
}

export const enum ScenarioType {
    Active = 'AUO',
    Passive = 'PO',
    Background = 'BGO'
}

@Injectable({
    providedIn: 'root'
})
export class VroomService extends XhrService implements OneDriveService {
    private static readonly WacDataSelectParams = ['id', 'openWith', 'officebundle', 'currentUserRole', 'eTag', 'name', 'size', 'content.downloadUrl', 'file', 'sharepointIds', 'sensitivityLabel', 'webUrl'];

    private readonly requestConfig: HttpRequestOptions;
    private readonly requestCache = new Map<string, VroomRequestCacheItem>();
    private readonly itemPathPredicate: string = ':/';
    private readonly myDriveUrl: string;
    private readonly isConsumer: boolean;
    private myDriveRootUrlStream: OperationStream<OneDriveRootUrls>;
    private myDriveRootUrlPromise: Promise<OneDriveRootUrls>;

    private static readonly mentionKey: string = 'mention';
    private static readonly commentKey: string = 'comment';
    private static readonly editKey: string = 'edit';
    private static readonly drivePath: string = 'drive/';
    private static readonly oneDriveBaseUrlSearchRegex = /^https?:\/\/(.(?!\/))*.\//i;
    private static readonly scenarioHeader: string = 'scenario';
    private static readonly scenarioTypeHeader: string = 'scenarioType';

    public static readonly sharesFileTypes = new Set([FileType.Excel, FileType.PowerPoint, FileType.Visio, FileType.Word, FileType.OneNote]);

    constructor(
        accessTokenService: AccessTokenService,
        private readonly capabilitiesService: CapabilitiesService,
        clientStartUpConfigService: ClientStartupConfigService,
        instrumentationService: InstrumentationService,
        instrumentedHttp: InstrumentedHttp,
        contextService: ContextService,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        private readonly dateService: DateService,
        private readonly windowService: WindowService,
        platformLocation: PlatformLocation,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        scheduler: SchedulerService
    ) {
        // We default to Sharepoint as the token type, but each API passes in a token type determined from the item URL.
        super(accessTokenService, instrumentationService, instrumentedHttp, AccessTokenConsumer.SharePoint, contextService, officeHomeApiConfigService, platformLocation, scheduler, 'WLID1.1 ');

        this.requestConfig = {
            headers: new Map([
                ['accept', 'application/json'],
                ['Prefer', 'allow-zerobyte'],
                ['application', 'officehome']
            ])
        };

        if (this.inMockMode) {
            // IIS won't allow the url format used by Vroom
            this.itemPathPredicate = '?path=';
        }

        const sharedContext = this.contextService.sharedContext;
        this.isConsumer = clientStartUpConfigService.config.authType === AuthType.MSA;
        this.myDriveUrl = `${this.isConsumer ? this.getConsumerBaseUrl() : this.getCommercialBaseUrl(sharedContext.oneDriveResourceId)}/${VroomService.drivePath}`;
    }

    public static getItemParams(selectItems: Array<string>, fetchWacData: boolean, fetchThumbnails: boolean, filter: string = '', action: OneDriveAction = 'open'): string {
        // Concat and dedupe wac data select params if applicable.
        const fullSelectParams = fetchWacData ? [...new Set(selectItems.concat(VroomService.WacDataSelectParams))] : selectItems;
        const selectStatement = fullSelectParams.length ? `$select=${fullSelectParams.join(',')}` : '';

        const filterStatement = filter ? `$filter=${filter}` : '';

        return [selectStatement, fetchThumbnails ? '$expand=thumbnails' : '', fetchWacData ? `action=${action}` : '', filterStatement].filter(Boolean).join('&');
    }

    public static getWopiHostDataFromItem(driveItem: GraphDriveItem): WopiHostData {
        return {
            bundleUrl: driveItem?.officeBundle?.url ?? '',
            bundleVersion: driveItem?.officeBundle?.version ?? '',
            downloadUrl: driveItem?.['@content.downloadUrl'] || driveItem?.['@microsoft.graph.downloadUrl'],
            eTag: driveItem?.eTag,
            fileName: driveItem?.name,
            fileSize: driveItem?.size,
            irmEnabled: driveItem?.sensitivityLabel?.protectionEnabled,
            isNewFile: false,
            readOnly: driveItem?.currentUserRole?.readOnly,
            wac: driveItem?.openWith?.wac,
            sharepointIds: driveItem?.sharepointIds
        };
    }

    public getMyDriveRootUrl(schedulePriority = SchedulePriority.Immediate): Promise<OneDriveRootUrls> {
        if (!this.capabilitiesService.capabilities.oneDriveApisEnabled) {
            return Promise.resolve<OneDriveRootUrls>(null);
        }

        if (!this.myDriveRootUrlPromise) {
            const rootUrlPromise = new Promise<OneDriveRootUrls>((resolve, reject) => {
                const rootUrlStream = this.getMyDriveRootUrlStream(schedulePriority);
                rootUrlStream.results.pipe(take(1)).toPromise().then(url => {
                    if (!url || !url.userRoot) {
                        reject('No/Incomplete data returned.');
                        return;
                    }

                    resolve(url);
                });

                rootUrlStream.initialCompletion.then(error => {
                    if (!error) {
                        return;
                    }

                    this.globalErrorHandler.handleCacheObservableError(error, ErrorId.Escyx, ErrorId.TBMsL);
                    if (error.cacheError && error.networkError) {
                        reject(error);
                    }
                });
            });

            this.myDriveRootUrlPromise = rootUrlPromise;
        }

        return this.myDriveRootUrlPromise;
    }

    public getMyDriveRootUrlStream(schedulePriority = SchedulePriority.Immediate): OperationStream<OneDriveRootUrls> {
        if (this.myDriveRootUrlStream) {
            return this.myDriveRootUrlStream;
        }

        const rootUrlFromCache = this.getMyDriveRootUrlInternal(FetchStrategy.CacheOnly, schedulePriority);
        const rootUrlFromNetwork = this.getMyDriveRootUrlInternal(FetchStrategy.NetworkFirst, schedulePriority);
        const rootUrlStream = ObservableHelper.createResultsWithCacheObservable(
            rootUrlFromCache,
            rootUrlFromNetwork,
            null,
            _ => this.myDriveRootUrlStream = null
        );

        this.myDriveRootUrlStream = rootUrlStream;
        return this.myDriveRootUrlStream;
    }

    public getSharedItemWithThumbnail(webUrl: string, documentType: FileType, fileFormat: string, strategy: FetchStrategy, fetchWacData: boolean, bypassMemoryCache = false, schedulePriority = SchedulePriority.Immediate): Promise<GraphDriveItem> {
        return this.getSharedItemWithParams(webUrl, documentType, fileFormat, VroomService.getItemParams(['id', 'sharepointIds', 'parentReference', 'createdDateTime'], fetchWacData, true), strategy, bypassMemoryCache, schedulePriority);
    }

    public getSharedItemWithoutThumbnail(webUrl: string, documentType: FileType, fileFormat: string, strategy: FetchStrategy, fetchWacData: boolean, action: OneDriveAction = 'open', bypassMemoryCache = false, schedulePriority = SchedulePriority.Immediate): Promise<GraphDriveItem> {
        return this.getSharedItemWithParams(webUrl, documentType, fileFormat, VroomService.getItemParams(['id', 'sharepointIds', 'parentReference', 'createdDateTime'], fetchWacData, false, '', action), strategy, bypassMemoryCache, schedulePriority);
    }

    public getSharedItemWithThumbnailAndIds(webUrl: string, documentType: FileType, fileFormat: string, strategy: FetchStrategy, fetchWacData: boolean, bypassMemoryCache = false, schedulePriority = SchedulePriority.Immediate): Promise<GraphDriveItem> {
        return this.getSharedItemWithParams(webUrl, documentType, fileFormat, VroomService.getItemParams(['id', 'sharepointIds'], fetchWacData, true), strategy, bypassMemoryCache, schedulePriority);
    }

    public getItemWithWacDataAndThumbnails(item: MruFileItem, strategy: FetchStrategy, bypassMemoryCache = false, schedulePriority = SchedulePriority.Immediate): Promise<GraphDriveItem> {
        return this.getItemWithParams(item.url, item.driveId, item.docId, strategy, VroomService.getItemParams([], true, true), bypassMemoryCache, schedulePriority);
    }

    public getItemWithWacData(docUrl: string, driveId: string, docId: string, strategy: FetchStrategy, action: OneDriveAction = 'open', bypassMemoryCache = false, schedulePriority = SchedulePriority.Immediate): Promise<GraphDriveItem> {
        return this.getItemWithParams(docUrl, driveId, docId, strategy, VroomService.getItemParams([], true, false, null, action), bypassMemoryCache, schedulePriority);
    }

    public getItemAccessForUser(docUrl: string, driveId: string, itemId: string, user: string) {
        const apiInfo = this.getApiInfo(docUrl, true /* use vroom 2.1 */);

        const url = `${apiInfo.baseUrl}/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}/oneDrive.checkAccess`;

        const checkAccessItemPromise = new Promise<CheckAccessItem>((resolve, reject) => {
            const callback = (promise: InstrumentedHttpPromise<CheckAccessResponse>) => {
                promise
                    .instrument((result, params) => {
                        params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                    })
                    .then(response => {
                        if (response && response.data && response.data.value && response.data.value[0]) {
                            resolve(response.data.value[0]);
                        } else {
                            reject(new XhrServiceError(response, 'No data returned'));
                        }
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.B95N9);
                        reject(error);
                    });
            };

            const checkAccessRequestConfig = this.getRequestConfig();
            checkAccessRequestConfig.headers.set('Content-Type', 'application/json');

            this.post(
                KnownInstrumentationEvents.VroomOneDriveCheckAccessRequest,
                KnownRequestInstrumentationEvents.VroomOneDriveCheckAccessRequest,
                url,
                JSON.stringify({ value: [{ email: user }] }),
                callback,
                reject,
                checkAccessRequestConfig,
                null,
                null,
                null,
                null,
                apiInfo.tokenType,
                apiInfo.resourceUrl,
                null,
                FetchStrategy.NetworkOnly,
                null,
                null
            );
        });

        return checkAccessItemPromise;
    }

    public getEnterpriseTemplatesLibraries(): Promise<GraphDriveItemCollection> {
        const params = VroomService.getItemParams(['id', 'name', 'organizationAssets'], false, false, 'organizationAssets/officeTemplateLibrary%20ne%20null');
        const url = `${this.getCommercialBaseUrl(this.contextService.sharedContext.sharePointResourceId, false, true)}/drives${params ? '?' + params : ''}`;

        return this.getDriveItems(KnownInstrumentationEvents.TemplateLibrariesRequest, KnownRequestInstrumentationEvents.TemplateLibrariesRequest, url, FetchStrategy.CacheOnly, this.contextService.sharedContext.sharePointResourceId, AccessTokenConsumer.SharePoint).catch(() =>
            this.getDriveItems(KnownInstrumentationEvents.TemplateLibrariesRequest, KnownRequestInstrumentationEvents.TemplateLibrariesRequest, url, FetchStrategy.NetworkFirst, this.contextService.sharedContext.sharePointResourceId, AccessTokenConsumer.SharePoint, SchedulePriority.Immediate, Scenario.BrowseOrgTemplates, ScenarioType.Active)
        );
    }

    public getDriveItemChildrenWithThumbnails(driveId: string, itemId?: string, fetchStrategy: FetchStrategy = FetchStrategy.NetworkFirst, scenario?: string, scenarioType?: ScenarioType): Promise<GraphDriveItemCollection> {
        if (!driveId) {
            return null;
        }

        const params = VroomService.getItemParams(['id', 'name', 'folder', 'webUrl', 'webDavUrl'], false, true);
        const path = itemId ? `items/${encodeURIComponent(itemId)}/` : 'root/';
        const url = `${this.getCommercialBaseUrl(this.contextService.sharedContext.sharePointResourceId)}/drives/${encodeURIComponent(driveId)}/${path}children${params ? '?' + params : ''}`;

        return this.getDriveItems(KnownInstrumentationEvents.VroomItemRequest, KnownRequestInstrumentationEvents.VroomItemRequest, url, fetchStrategy, this.contextService.sharedContext.sharePointResourceId, AccessTokenConsumer.SharePoint, SchedulePriority.Immediate, scenario, scenarioType);
    }

    // TODO: Rename Graph... models that mirror Vroom models
    public createNewFile(filename: string, fileType: FileType, fetchOpenWithData = false): Promise<GraphDriveItem> {
        const url = `${this.myDriveUrl}root/children?select=webDavUrl,${fetchOpenWithData ? 'openWith,' : ''}*`;
        const postBody = {
            'name': filename,
            'file': {},
            '@name.conflictBehavior': 'rename'
        };

        return new Promise<GraphDriveItem>((resolve, reject) => {
            this.post<GraphDriveItem | GraphError>(
                KnownInstrumentationEvents.VroomCreateFileRequest,
                KnownRequestInstrumentationEvents.VroomCreateFileRequest,
                url,
                postBody,
                (promise: InstrumentedHttpPromise<GraphDriveItem | GraphError>) => {
                    promise
                        .instrument((result, params) => {
                            params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                        })
                        .then(response => {
                            const item = response.data as GraphDriveItem;
                            if (item && item.id) {
                                resolve(item);
                            } else {
                                reject(new XhrServiceError(response, 'Could not parse drive item from Vroom response'));
                            }
                        })
                        .catch((error: InstrumentedHttpDataResponse<GraphDriveItem | GraphError>) => {
                            // TODO: remove once MSA errors are figured out
                            if (error && error.data) {
                                const params = {};
                                params[InstrumentationParam.DebugInfo] = JSON.stringify(error.data);
                                params[InstrumentationParam.HttpCode] = error.status.toString();
                                this.instrumentationService.logDebugCacheEvent(KnownInstrumentationEvents.VroomCreateFileRequest, params);
                                logDiagnostic({eventName: KnownInstrumentationEvents.VroomCreateFileRequest, params});
                            }

                            this.globalErrorHandler.handleError(error, ErrorId.vcXyA);
                        });
                },
                (xhrError: XhrServiceError<GraphDriveItem | GraphError>) => {
                    if (xhrError && xhrError.httpResponse) {
                        if (xhrError.httpResponse.data) {
                            reject({ error: { code: `${xhrError.httpResponse.status}`, data: `${xhrError.httpResponse.data}` } });
                        } else {
                            reject({ error: { code: `${xhrError.httpResponse.status}` } });
                        }
                    } else {
                        reject();
                    }
                },
                this.getRequestConfig(),
                null,
                null,
                null,
                null,
                AccessTokenConsumer.OneDrive,
                this.contextService.sharedContext.oneDriveResourceId,
                null,
                null,
                true,
                { category: ErrorDialogErrorCategory.CreateFile, fileType }
            );
        });
    }

    // Uploads an entire file to OneDrive
    public uploadFile(
        file: File,
        sessionId: string,
        parent?: string,
        path?: string,
        onProgress?: (progressEvent: HttpProgressEvent) => void,
        cancellationToken?: PromiseCancellationToken,
        timeoutInMs?: number
    ): Promise<GraphDriveItem> {
        return new Promise<GraphDriveItem>((resolve, reject) => {
            if (!file || !file.name) {
                reject(new XhrServiceError(null, 'Invalid file'));
            }

            const url = `${this.getUploadLocation(this.myDriveUrl, file, parent, path)}content?@name.conflictBehavior=rename&select=webDavUrl,*`;
            this.put<GraphDriveItem | GraphError>(
                KnownInstrumentationEvents.VroomUploadFileRequest,
                KnownRequestInstrumentationEvents.VroomUploadFileRequest,
                url,
                file,
                promise => {
                    promise
                        .instrument((result, params) => this.instrumentUploadResponse(result, params, file, sessionId))
                        .then(response => {
                            const item = response.data as GraphDriveItem;
                            if (item && item.id) {
                                resolve(item);
                            } else {
                                reject(new XhrServiceError(response, 'Could not parse drive item from Vroom response'));
                            }
                        })
                        .catch(error => {
                            this.globalErrorHandler.handleError(error, ErrorId.dSenI);
                        });
                },
                reject,
                this.getRequestConfig(),
                onProgress,
                null,
                cancellationToken,
                timeoutInMs,
                AccessTokenConsumer.OneDrive,
                this.contextService.sharedContext.oneDriveResourceId);
        });
    }

    public getDownloadUrl(getDownloadUrlProps: GetDownloadUrlProps): Promise<string> {
        if (!getDownloadUrlProps || !getDownloadUrlProps.itemId || !getDownloadUrlProps.driveId || !getDownloadUrlProps.itemUrl) {
            return Promise.reject('Invalid params');
        }

        const params = VroomService.getItemParams(['id', 'content.downloadUrl'], false, false);
        return this.getItemWithParams(getDownloadUrlProps.itemUrl, getDownloadUrlProps.driveId, getDownloadUrlProps.itemId, FetchStrategy.NetworkOnly, params)
            .then(driveItem => {
                if (!driveItem['@content.downloadUrl']) {
                    return Promise.reject('Missing downloadUrl');
                }

                return UrlBuilder.removeQueryParams(driveItem['@content.downloadUrl'], ['tempAuth', 'ApiVersion'], false);
            });
    }

    // PWA TODO: Need to support canonical URLs for opening large files after uploading
    // Creates a new upload session for the requested file
    public createUploadSession(file: File, sessionId: string, parent?: string, path?: string, cancellationToken?: PromiseCancellationToken): Promise<GraphUploadSession> {
        return new Promise<GraphUploadSession>((resolve, reject) => {
            if (!file || !file.name) {
                reject(new XhrServiceError(null, 'Invalid file'));
            }
            const url = `${this.getUploadLocation(this.myDriveUrl, file, parent, path)}${this.inMockMode ? '' : 'oneDrive.'}createUploadSession`;
            const postBody = {
                item: {
                    '@name.conflictBehavior': 'rename'
                }
            };

            this.post<GraphUploadSession | GraphError>(
                KnownInstrumentationEvents.VroomCreateUploadSessionRequest,
                KnownRequestInstrumentationEvents.VroomCreateUploadSessionRequest,
                url,
                postBody,
                promise => {
                    promise
                        .instrument((result, params) => {
                            this.instrumentUploadResponse(result, params, file, sessionId);
                            if (result.ok) {
                                const session = result.data as GraphUploadSession;
                                params[InstrumentationParam.Length] = (session && session.uploadUrl) ? session.uploadUrl.length.toString() : '0';
                            }
                        })
                        .then(response => {
                            const session = response.data as GraphUploadSession;
                            if (session && session.uploadUrl) {
                                resolve(session);
                            } else {
                                reject(new XhrServiceError(response, 'Could not parse upload session from Vroom response'));
                            }
                        })
                        .catch(error => {
                            this.globalErrorHandler.handleError(error, ErrorId.d9WE9);
                        });
                },
                reject,
                this.getRequestConfig(),
                null,
                null,
                cancellationToken,
                null,
                AccessTokenConsumer.OneDrive,
                this.contextService.sharedContext.oneDriveResourceId
            );
        });
    }

    public cancelUploadSession(sessionUrl: string, sessionId: string, cancellationToken?: PromiseCancellationToken): Promise<void> {
        return this.cancelUploadSessionInternal(sessionUrl, sessionId, cancellationToken, AccessTokenConsumer.OneDrive, this.contextService.sharedContext.oneDriveResourceId);
    }

    // Uploads a file fragment to an existing upload session
    // Note: startByte is the index of the first byte to be sent, while endByte is the first index not included in the fragment (ex. first 10 byte fragment would be (0, 10))
    public uploadFragment(
        sessionUrl: string,
        startByte: number,
        endByte: number,
        fileSize: number,
        content: Blob,
        sessionId: string,
        onProgress?: (progressEvent: HttpProgressEvent) => void,
        cancellationToken?: PromiseCancellationToken,
        timeout?: number
    ): Promise<GraphUploadSessionStatus | GraphDriveItem> {
        return new Promise<GraphUploadSessionStatus | GraphDriveItem>((resolve, reject) => {
            if (!sessionUrl) {
                reject(new XhrServiceError(null, 'Invalid session url'));
            }

            // Upload API issues a tempauth token in the query string parameter, which can only be used if URL matches "...uploadSession?guid=<guid>..."
            // In order to get the canonical URL, we would then have to append "select=webDavUrl,*" as a parameter towards the end. Subsequent requests to
            // the API would then automatically contain the QS param as well.
            const selectQuery = 'select=webDavUrl,*';
            if (!sessionUrl.endsWith(selectQuery)) {
                sessionUrl = UrlBuilder.appendQuery(sessionUrl, selectQuery);
            }

            const uploadFragmentHeaders = new Map<string, string | Array<string>>();
            const contentLength = Math.max(0, endByte - startByte);
            uploadFragmentHeaders.set('Content-Range',
                `bytes ${startByte}-${startByte + contentLength - 1}/${fileSize}`);
            const uploadFragmentConfig: HttpRequestOptions = {
                headers: uploadFragmentHeaders
            };

            this.put<GraphUploadSessionStatus | GraphDriveItem | GraphError>(
                KnownInstrumentationEvents.OneDriveGraphUploadFragmentRequest,
                KnownRequestInstrumentationEvents.OneDriveGraphUploadFragmentRequest,
                sessionUrl,
                content,
                promise => {
                    promise
                        .instrument((result, params) => {
                            params[InstrumentationParam.TotalSize] = fileSize.toString();
                            params[InstrumentationParam.ActualSize] = contentLength.toString();
                            params[InstrumentationParam.Index] = startByte.toString();
                            params[InstrumentationParam.UploadSessionId] = sessionId;
                            params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                        })
                        .then(({ data }) => resolve(data as (GraphUploadSessionStatus | GraphDriveItem)))
                        .catch(error => {
                            this.globalErrorHandler.handleError(error, ErrorId.t59Ym);
                        });
                },
                reject,
                uploadFragmentConfig,
                onProgress,
                null,
                cancellationToken,
                timeout,
                AccessTokenConsumer.None
            );
        });
    }

    public getUploadSessionStatus(sessionUrl: string, sessionId: string, cancellationToken: PromiseCancellationToken): Promise<GraphDriveItem | GraphUploadSessionStatus> {
        return this.getUploadSessionStatusInternal(sessionUrl, sessionId, cancellationToken, AccessTokenConsumer.OneDrive, this.contextService.sharedContext.oneDriveResourceId);
    }

    public getDocumentActivity(item: MruFileItem, strategy: FetchStrategy, schedulePriority = SchedulePriority.Immediate): Promise<Array<Activity>> {
        if (!item || !item.driveId || !item.docId) {
            return Promise.reject<Array<Activity>>(new XhrServiceError(null, 'Drive and doc IDs are required'));
        }

        const key = `${strategy}_${item.driveId}_${item.docId}_activity`;
        const cachedResponse = this.getRequestFromCache(key);
        if (cachedResponse) {
            return cachedResponse;
        }

        const promise = new Promise<Array<Activity>>((resolve, reject) => {
            const promiseCallback = (instrumentedPromise: InstrumentedHttpPromise<VroomActivityResponse>) => {
                instrumentedPromise
                    .instrument((result, params) => {
                        params[InstrumentationParam.RequestId] = this.getSharepointRequestId(result);
                        params[InstrumentationParam.FileFormat] = MruFileFormat[item.format];
                        if (result && result.ok && result.data && result.data.value) {
                            const itemCount = result.data.value.length.toString();
                            params[InstrumentationParam.ActualSize] = itemCount;
                            params[InstrumentationParam.Distribution] = this.distributionByActionType(result.data.value);
                        }
                    })
                    .then(response => {
                        if (response && response.data && response.data.value) {
                            resolve(response.data.value.map(vroomActivity => {
                                let action: Action;
                                if (vroomActivity.action.hasOwnProperty(VroomService.mentionKey)) {
                                    action = { type: ActionType.Mention, data: vroomActivity.action[VroomService.mentionKey] };
                                } else if (vroomActivity.action.hasOwnProperty(VroomService.commentKey)) {
                                    action = { type: ActionType.Comment, data: vroomActivity.action[VroomService.commentKey] };
                                } else if (vroomActivity.action.hasOwnProperty(VroomService.editKey)) {
                                    action = { type: ActionType.Edit, data: vroomActivity.action[VroomService.editKey] };
                                } else {
                                    action = { type: ActionType.Unknown, data: null };
                                }

                                return {
                                    action,
                                    actor: vroomActivity.actor,
                                    timestamp: this.getPreferredTime(vroomActivity.times)
                                };
                            }));
                        } else {
                            reject(new XhrServiceError(response, 'No data returned'));
                        }
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.ACZGk);
                    });
            };

            const finalErrorCallback = error => {
                this.requestCache.delete(key);
                reject(error);
            };

            const apiInfo = this.getCommercialApiInfo(item.url);
            if (!apiInfo) {
                reject('Invalid document url');
            }

            const url = `${apiInfo.baseUrl}/drives/${encodeURIComponent(item.driveId)}/items/${encodeURIComponent(item.docId)}/activities`;
            this.get(
                KnownInstrumentationEvents.VroomActivityRequest,
                KnownRequestInstrumentationEvents.VroomActivityRequest,
                url,
                promiseCallback,
                finalErrorCallback,
                this.getRequestConfig(),
                strategy,
                null,
                null,
                null,
                apiInfo.tokenType,
                apiInfo.resourceUrl,
                schedulePriority
            );
        });

        this.requestCache.set(key, { promise, expiryTime: this.dateService.addSeconds(this.dateService.getCurrentDate(), this.contextService.sharedContext.activityLifetimeInSeconds) });

        return promise;
    }

    public getUploadLocation(drivePath: string, file: File, parent?: string, path?: string): string {
        const destination = path ? `root${this.itemPathPredicate}${path}/` : parent ? `items/${parent}${this.itemPathPredicate}` : `root${this.itemPathPredicate}`;
        return `${drivePath}${this.inMockMode ? 'upload/' : `${destination}${encodeURIComponent(file.name)}${this.itemPathPredicate}`}`;
    }

    public instrumentUploadResponse(
        result: InstrumentedHttpDataResponse<GraphUploadSession | GraphDriveItem | GraphError>,
        params: InstrumentationParams | TelemetryProperties,
        file: File,
        sessionId: string
    ) {
        params[InstrumentationParam.TotalSize] = file.size.toString();
        params[InstrumentationParam.UploadSessionId] = sessionId;
        params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
    }

    public getRootUrlsFromRootItem(rootItem: GraphDriveItem, strategy: FetchStrategy) {
        let tenantOneDriveRootUrl: string = null;
        const userOneDriveRootUrl = rootItem ? rootItem.webUrl : null;

        if (!userOneDriveRootUrl) {
            this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.OneDrivePlacesBadUrl);

            return null;
        }

        if (!this.isConsumer) {
            // ex. https://contoso-my.sharepoint.com/personal/jsmith_contoso_com/documents
            const oneDriveRootMatches = VroomService.oneDriveBaseUrlSearchRegex.exec(userOneDriveRootUrl);
            if (oneDriveRootMatches && oneDriveRootMatches.length) {
                tenantOneDriveRootUrl = oneDriveRootMatches[0];
            }
        }

        return { tenantRoot: tenantOneDriveRootUrl, userRoot: userOneDriveRootUrl, fromCache: strategy === FetchStrategy.CacheOnly };
    }

    public getSharesUrl(webUrl: string): string {
        try {
            let encodedUrl = this.windowService.nativeWindow.btoa(unescape(encodeURIComponent(webUrl)));
            let i = encodedUrl.length - 1;
            while (encodedUrl.charAt(i) === '=') {
                --i;
            }
            encodedUrl = encodedUrl.slice(0, i + 1).replace(/\+/g, '-').replace(/\//g, '_');
            return `shares/u!${encodedUrl}/driveItem`;
        } catch (error) {
            this.globalErrorHandler.handleError(error, ErrorId.fYwwF);
            return null;
        }
    }

    // Cancels an existing upload session
    public cancelUploadSessionInternal(
        sessionUrl: string,
        sessionId: string,
        cancellationToken: PromiseCancellationToken,
        tokenConsumerOverride: AccessTokenConsumer,
        resourceUrl?: string
    ): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (!sessionUrl) {
                reject(new XhrServiceError(null, 'Invalid session url'));
            }

            this.delete<void | GraphError>(
                KnownInstrumentationEvents.OneDriveGraphCancelUploadSessionRequest,
                KnownRequestInstrumentationEvents.OneDriveGraphCancelUploadSessionRequest,
                sessionUrl,
                null,
                promise => {
                    promise
                        .instrument((result, params) => {
                            params[InstrumentationParam.UploadSessionId] = sessionId;
                            params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                        })
                        .then(() => resolve(null))
                        .catch(error => {
                            this.globalErrorHandler.handleError(error, ErrorId.wQvvW);
                        });
                },
                reject,
                this.getRequestConfig(),
                cancellationToken,
                null,
                tokenConsumerOverride,
                resourceUrl
            );
        });
    }

    // Retrieves the status of an existing upload session
    public getUploadSessionStatusInternal(
        sessionUrl: string,
        sessionId: string,
        cancellationToken: PromiseCancellationToken,
        tokenConsumerOverride: AccessTokenConsumer,
        resourceUrl?: string
    ): Promise<GraphDriveItem | GraphUploadSessionStatus> {
        return new Promise<GraphUploadSessionStatus | GraphDriveItem>((resolve, reject) => {
            if (!sessionUrl) {
                reject(new XhrServiceError(null, 'Invalid session url'));
            }

            this.get<GraphUploadSessionStatus | GraphError>(
                KnownInstrumentationEvents.OneDriveGraphUploadStatusRequest,
                KnownRequestInstrumentationEvents.OneDriveGraphUploadStatusRequest,
                sessionUrl,
                promise => {
                    promise
                        .instrument((result, params) => {
                            params[InstrumentationParam.UploadSessionId] = sessionId;
                            params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                        })
                        .then(({ data }) => resolve(data as GraphUploadSessionStatus))
                        .catch(error => {
                            this.globalErrorHandler.handleError(error, ErrorId.PPAU2);
                        });
                },
                reject,
                this.getRequestConfig(),
                FetchStrategy.NetworkOnly,
                null,
                cancellationToken,
                null,
                tokenConsumerOverride,
                resourceUrl
            );
        });
    }

    public getMyDriveRoot(strategy: FetchStrategy, schedulePriority: SchedulePriority): Promise<GraphDriveItem> {
        return this.getMyDriveItem('root', strategy, schedulePriority);
    }

    public createSharingLink(driveId: string, itemId: string): Promise<string> {
        if (this.isConsumer) {
            return Promise.reject('api only available for commercial');
        }

        const url = `${this.getCommercialBaseUrl(this.contextService.sharedContext.sharePointResourceId, false, true)}/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}/createLink`;
        const body = {
            type: 'addressBar'
        };

        const createLinkRequestConfig = this.getRequestConfig();
        createLinkRequestConfig.headers.set('Content-Type', 'application/json');
        const createLinkPromise = new Promise<string>((resolve, reject) => {
            const callback = (promise: InstrumentedHttpPromise<VroomCreateLinkResponse>) => {
                promise
                    .instrument((result, params) => {
                        params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                    })
                    .then(response => {
                        if (response?.ok && response?.data?.link?.webUrl) {
                            resolve(response.data.link.webUrl);
                            return;
                        }
                        reject(response.statusText);
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error);
                    });
            };

            this.post(
                KnownInstrumentationEvents.VroomCreateLinkRequest,
                KnownRequestInstrumentationEvents.VroomCreateLinkRequest,
                url,
                JSON.stringify(body),
                callback,
                reject,
                createLinkRequestConfig
            );
        });

        return createLinkPromise;
    }

    public getThumbnailString(url: string): Promise<string> {
        const reader = new FileReader();

        return new Promise<string>((resolve, reject) => {
            const promiseCallback = (promise: InstrumentedHttpPromise<Blob>) => {
                promise.instrument((result, params) => {
                    params[InstrumentationParam.RequestId] = this.getSharepointRequestId(result);
                }).then(data => {
                    if (data && data.data) {
                        reader.onloadend = () => {
                            const result = (typeof reader.result === 'string' && reader.result) || '';
                            if (result.length > 0) {
                                resolve(result);
                            } else {
                                reject();
                            }
                        };
                        reader.onerror = reject;
                        reader.onabort = reject;
                        reader.readAsDataURL(data.data);
                    } else {
                        reject();
                    }
                }).catch(error => {
                    this.globalErrorHandler.handleError(error, ErrorId.AfEUN);
                });
            };
            url = `${this.isConsumer ? this.getConsumerBaseUrl() : this.getCommercialBaseUrl(this.contextService.sharedContext.oneDriveResourceId)}/${url}`;

            const blobRequestConfig = this.getRequestConfig({
                responseType: HttpResponseContentType.Blob
            });
            const apiInfo = this.getApiInfo(url);

            this.get(
                KnownInstrumentationEvents.VroomThumbnailStringRequest,
                KnownRequestInstrumentationEvents.VroomThumbnailStringRequest,
                url,
                promiseCallback,
                reject,
                blobRequestConfig,
                FetchStrategy.NetworkOnly, null, null, null, apiInfo.tokenType, apiInfo.resourceUrl
            );
        });
    }

    public getFluidEnabled(fetchStrategy: FetchStrategy): Promise<boolean> {
        const url = `${this.getCommercialBaseUrl(this.contextService.sharedContext.sharePointResourceId, false, true)}/${VroomService.drivePath}root/opStream/fluidEnabled`;
        return new Promise<boolean>((resolve, reject) => {
            this.get<VroomFluidEnabled>(
                KnownInstrumentationEvents.VroomGetFluidEnabled,
                KnownRequestInstrumentationEvents.VroomGetFluidEnabled,
                url,
                promise => {
                    promise
                        .then(({ data }) => resolve(data.isFluidEnabled))
                        .catch(error => {
                            this.globalErrorHandler.handleError(error, ErrorId.PPAU2);
                            reject();
                        });
                },
                reject,
                this.getRequestConfig(),
                fetchStrategy,
                null,
                null,
                null,
                AccessTokenConsumer.SharePoint,
                this.contextService.sharedContext.sharePointResourceId
            );
        });
    }

    protected handleQueryParam(paramNameInLowerCase: string, value: string | Array<string>, requestConfig: HttpRequestOptions) {
        if (paramNameInLowerCase === 'mockvroom' && this.mockRequestHeader) {
            requestConfig?.headers.set(this.mockRequestHeader, value);
        }
    }

    protected getRequestIdFromResponse(response: InstrumentedHttpDataResponse<any>): string {
        if (this.isConsumer) {
            // TODO: OneDrive still needs to add the 'ms-cv' header to their Access-Control-Expose-Headers list
            return this.getHeaderValue(response.headers, 'ms-cv');
        }

        // If the response headers don't have "request-id" try "SPRequestGuid"
        return super.getRequestIdFromResponse(response) ?? this.getSharepointRequestId(response);
    }

    private getMyDriveItem(url: string, strategy: FetchStrategy, schedulePriority: SchedulePriority): Promise<GraphDriveItem> {
        return this.getDriveItem(KnownInstrumentationEvents.VroomItemRequest, KnownRequestInstrumentationEvents.VroomItemRequest, `${this.myDriveUrl}${url}`, strategy, null, AccessTokenConsumer.OneDrive, this.contextService.sharedContext.oneDriveResourceId, schedulePriority);
    }

    private getItemWithParams(docUrl: string, driveId: string, docId: string, strategy: FetchStrategy, params: string, bypassMemoryCache = false, schedulePriority = SchedulePriority.Immediate): Promise<GraphDriveItem> {
        const apiInfo = this.getApiInfo(docUrl);
        if (!apiInfo) {
            return Promise.reject('Invalid document url');
        }

        const url = `${apiInfo.baseUrl}/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(docId)}/${params ? '?' + params : ''}`;
        const isWacHostRequest = params.includes('openWith');
        return this.getDriveItem(
            isWacHostRequest ? KnownInstrumentationEvents.VroomItemWithWacDataRequest : KnownInstrumentationEvents.VroomItemRequest,
            isWacHostRequest ? KnownRequestInstrumentationEvents.VroomItemWithWacDataRequest : KnownRequestInstrumentationEvents.VroomItemRequest,
            url,
            strategy,
            null,
            apiInfo.tokenType,
            apiInfo.resourceUrl,
            schedulePriority,
            isWacHostRequest,
            bypassMemoryCache
        );
    }

    // NOTE: The SchedulePriority is set for this method assuming that it is only used to inflate MruItems.
    private getSharedItemWithParams(webUrl: string, documentType: FileType, fileFormat: string, params: string, strategy: FetchStrategy, bypassMemoryCache = false, schedulePriority = SchedulePriority.Immediate): Promise<GraphDriveItem> {
        const shareUrl = this.getSharesUrl(webUrl);
        if (!shareUrl) {
            return Promise.reject<GraphDriveItem>(new XhrServiceError(null, 'Failed to construct shares url'));
        }

        const apiInfo = this.getApiInfo(webUrl);

        const endpoint = this.inMockMode
            ? `shares?itemUrl=${encodeURIComponent(shareUrl)}&currentDate=${this.dateService.getCurrentDateMinuteGranularity().toISOString()}`
            : `${shareUrl}${params ? '?' + params : ''}`;

        const url = `${apiInfo.baseUrl}/${endpoint}`;
        const isWacHostRequest = params.includes('openWith');

        return this.getDriveItem(
            isWacHostRequest ? KnownInstrumentationEvents.VroomSharesWithWacDataRequest : KnownInstrumentationEvents.VroomSharesRequest,
            isWacHostRequest ? KnownRequestInstrumentationEvents.VroomSharesWithWacDataRequest : KnownRequestInstrumentationEvents.VroomSharesRequest,
            url,
            strategy,
            (result, instrumentationParams) => {
                instrumentationParams[InstrumentationParam.RequestId] = this.getSharepointRequestId(result);
                instrumentationParams[InstrumentationParam.DocumentType] = FileType[documentType];
                if (fileFormat) {
                    instrumentationParams[InstrumentationParam.FileFormat] = fileFormat;
                }

                if (result.ok) {
                    const item = result.data as GraphDriveItem;
                    const thumbnails = item ? item.thumbnails : null;
                    instrumentationParams[InstrumentationParam.ItemCount] = (thumbnails ? thumbnails.length : 0).toString();
                }
            },
            apiInfo.tokenType,
            apiInfo.resourceUrl,
            schedulePriority,
            isWacHostRequest,
            bypassMemoryCache);
    }

    private getDriveItem(
        requestName: KnownInstrumentationEvents,
        otelRequestName: KnownRequestInstrumentationEvents,
        url: string,
        strategy: FetchStrategy,
        instrumentCallback?: InstrumentedHttpPromiseCallback<GraphDriveItem | GraphError>,
        tokenConsumerOverride?: AccessTokenConsumer,
        resourceUrl?: string,
        schedulePriority = SchedulePriority.Immediate,
        fetchingWacData = false,
        bypassMemoryCache = false
    ): Promise<GraphDriveItem> {
        const cacheKey = `${url}/${strategy}`;
        const cachedResponse = !bypassMemoryCache ? this.getRequestFromCache(cacheKey) : undefined;
        if (cachedResponse) {
            return cachedResponse;
        }

        const itemPromise = new Promise<GraphDriveItem>((resolve, reject) => {
            const callback = (promise: InstrumentedHttpPromise<GraphDriveItem | GraphError>) => {
                promise
                    .instrument((result, params) => {
                        params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);

                        if (instrumentCallback) {
                            instrumentCallback(result, params);
                        }
                    })
                    .then(response => {
                        if (response && response.data) {
                            resolve(response.data as GraphDriveItem);
                        } else {
                            reject(new XhrServiceError(response, 'No data returned'));
                        }
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.tIvZK);
                    });
            };

            const errorCallback = (error: XhrServiceError<GraphDriveItem | GraphError>) => {
                this.requestCache.delete(cacheKey);
                reject(error);
            };

            const requestConfig = this.getRequestConfig();

            if (requestConfig.headers.has(VroomService.scenarioHeader)) {
                requestConfig.headers.delete(VroomService.scenarioHeader);
            }

            if (requestConfig.headers.has(VroomService.scenarioTypeHeader)) {
                requestConfig.headers.delete(VroomService.scenarioTypeHeader);
            }

            this.get(
                requestName,
                otelRequestName,
                url,
                callback,
                errorCallback,
                requestConfig,
                strategy,
                null,
                null,
                null,
                tokenConsumerOverride,
                resourceUrl,
                schedulePriority
            );
        });

        // If this request contains wac data we have to invalid it after 10 hours (wopi token expiration time)
        this.requestCache.set(cacheKey, { promise: itemPromise, expiryTime: fetchingWacData ? this.dateService.addHours(this.dateService.getCurrentDate(), 10) : undefined });
        return itemPromise;
    }

    private getDriveItems(
        requestName: KnownInstrumentationEvents,
        otelRequestName: KnownRequestInstrumentationEvents,
        url: string,
        strategy: FetchStrategy,
        resourceUrl: string,
        tokenConsumerOverride: AccessTokenConsumer,
        schedulePriority = SchedulePriority.Immediate,
        scenario?: string,
        scenarioType?: ScenarioType
    ): Promise<GraphDriveItemCollection> {
        const cacheKey = `${url}/${strategy}`;
        const cachedResponse = this.getRequestFromCache(cacheKey);
        if (cachedResponse) {
            return cachedResponse;
        }

        const itemPromise = new Promise<GraphDriveItemCollection>((resolve, reject) => {
            const callback = (promise: InstrumentedHttpPromise<GraphDriveItemCollection | GraphError>) => {
                promise
                    .instrument((result, params) => {
                        params[InstrumentationParam.RequestId] = this.getSharepointRequestId(result);
                    })
                    .then(response => {
                        if (response && response.data) {
                            resolve(response.data as GraphDriveItemCollection);
                        } else {
                            reject(new XhrServiceError(response, 'No data returned'));
                        }
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.QYFpr);
                    });
            };

            const errorCallback = (error: XhrServiceError<GraphDriveItemCollection | GraphError>) => {
                this.requestCache.delete(cacheKey);
                reject(error);
            };

            const requestConfig = this.getRequestConfig();

            if (scenario) {
                if (requestConfig.headers.has(VroomService.scenarioHeader)) {
                    requestConfig.headers[VroomService.scenarioHeader] = scenario;
                } else {
                    requestConfig.headers.set(VroomService.scenarioHeader, scenario);
                }
            } else if (requestConfig.headers.has(VroomService.scenarioHeader)) {
                requestConfig.headers.delete(VroomService.scenarioHeader);
            }

            if (scenarioType) {
                if (requestConfig.headers.has(VroomService.scenarioTypeHeader)) {
                    requestConfig.headers[VroomService.scenarioTypeHeader] = scenarioType;
                } else {
                    requestConfig.headers.set(VroomService.scenarioTypeHeader, scenarioType);
                }
            } else if (requestConfig.headers.has(VroomService.scenarioTypeHeader)) {
                requestConfig.headers.delete(VroomService.scenarioTypeHeader);
            }

            this.get(
                requestName,
                otelRequestName,
                url,
                callback,
                errorCallback,
                requestConfig,
                strategy,
                null,
                null,
                null,
                tokenConsumerOverride,
                resourceUrl,
                schedulePriority
            );
        });

        this.requestCache.set(cacheKey, { promise: itemPromise });
        return itemPromise;
    }

    private distributionByActionType(activities: Array<VroomActivity>): string {
        if (!activities) {
            return '';
        }

        return activities.map(activity => {
            if (activity && activity.action) {
                const keys = Object.keys(activity.action);
                if (keys && keys.length) {
                    return keys[0];
                }
            }

            return '';
        }).reduce((combined, current, index) => combined + (index === 0 ? '' : ';') + current, '');
    }

    private getPreferredTime(vroomTimes: VroomActivityTimes): Date {
        if (!vroomTimes) {
            return null;
        }

        return vroomTimes.observedTime ? new Date(vroomTimes.observedTime) :
            (vroomTimes.recordedTime ? new Date(vroomTimes.recordedTime) : null);
    }

    private getRequestFromCache(key: string): Promise<any> {
        if (this.requestCache.has(key)) {
            const cacheItem = this.requestCache.get(key);
            if (cacheItem.promise && (!cacheItem.expiryTime || cacheItem.expiryTime > this.dateService.getCurrentDate())) {
                return cacheItem.promise;
            } else {
                this.requestCache.delete(key);
            }
        }

        return null;
    }

    private getApiInfo(itemUrl: string, useTwoPointOne?: boolean): VroomApiInfo {
        return this.isConsumer ? this.getConsumerApiInfo(itemUrl) : this.getCommercialApiInfo(itemUrl, useTwoPointOne);
    }

    private getConsumerApiInfo(itemUrl: string): VroomApiInfo {
        const parsedUrl = UrlParser.getLocation(itemUrl);
        if (!parsedUrl || !parsedUrl.protocol || !parsedUrl.host) {
            return null;
        }

        const baseUrl = this.getConsumerBaseUrl();
        const tokenType = AccessTokenConsumer.OneDrive;
        const resourceUrl = this.contextService.sharedContext.oneDriveResourceId;

        return {
            baseUrl,
            tokenType,
            resourceUrl,
            hostname: parsedUrl.hostname
        };
    }

    private getCommercialApiInfo(itemUrl: string, useTwoPointOne: boolean = false): VroomApiInfo {
        const parsedUrl = UrlParser.getLocation(itemUrl);
        if (!parsedUrl || !parsedUrl.protocol || !parsedUrl.host) {
            return null;
        }

        const resourceUrl = `${parsedUrl.protocol}//${parsedUrl.host}`;

        const baseUrl = this.getCommercialBaseUrl(resourceUrl, false, useTwoPointOne);
        const tokenType = this.getTokenType(resourceUrl);
        return {
            baseUrl,
            tokenType,
            resourceUrl,
            hostname: parsedUrl.hostname
        };
    }

    private getCommercialBaseUrl(rootUrl: string, isNotVersioned?: boolean, useVroomTwoPointOne: boolean = false) {
        rootUrl = this.inMockMode ? `${this.contextService.sharedContext.officeHomeApiRoot}` : rootUrl;

        if (isNotVersioned) {
            return `${rootUrl}/_api`;
        } else if (useVroomTwoPointOne) {
            return `${rootUrl}/_api/v2.1`;
        } else {
            return `${rootUrl}/_api/v2.0`;
        }
    }

    private getConsumerBaseUrl() {
        return this.inMockMode ? this.contextService.sharedContext.officeHomeApiRoot : this.contextService.sharedContext.oneDriveConsumerApi;
    }

    private getTokenType(url: string): AccessTokenConsumer {
        const referralType = FileItem.referralFromFileLocation(url);
        if (referralType === KnownWorkloadReferralNames.OneDrive) {
            return AccessTokenConsumer.OneDrive;
        }

        return AccessTokenConsumer.SharePoint;
    }

    private getRequestConfig(overrides: HttpRequestOptions = {}): HttpRequestOptions {
        // Copy config so changes don't persist across requests
        return {
            ...this.requestConfig,
            headers: new Map(this.requestConfig.headers),
            ...overrides
        };
    }

    // The activity API does not expose the request-id header through CORS, but they do
    // expose the SPRequestGuid header, which seems to have the same value as request-id.
    private getSharepointRequestId(response: InstrumentedHttpDataResponse<any>): string {
        return response.headers && response.headers.get('SPRequestGuid');
    }

    private getMyDriveRootUrlInternal(strategy: FetchStrategy, schedulePriority: SchedulePriority): Promise<OneDriveRootUrls> {
        return this.getMyDriveRoot(strategy, schedulePriority).then(rootItem =>
            this.getRootUrlsFromRootItem(rootItem, strategy)
        );
    }
}
